import { apiUrl, setSession } from '@/api/auth';

/**
 * Browser-side WebAuthn helpers.
 *
 * The server encodes binary payloads (challenge, credential id, public key,
 * etc.) as base64url strings. These helpers convert between base64url and
 * `ArrayBuffer` and wrap the two native ceremonies so pages only deal with a
 * typed, JSON-serialisable credential object.
 */

/**
 * WebAuthn goes to the absolute API origin (see `apiUrl`): login/verify sets
 * the host-only HttpOnly refresh cookie, which the Vite dev proxy would store
 * on the tools host instead.
 */
function webauthnURL(path: string): string {
  return apiUrl(path);
}

function base64UrlToBuffer(input: string): ArrayBuffer {
  const padding = '='.repeat((4 - (input.length % 4)) % 4);
  const b64 = (input + padding).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(b64);
  const buf = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < binary.length; i += 1) view[i] = binary.charCodeAt(i);
  return buf;
}

function bufferToBase64Url(buffer: ArrayBuffer | ArrayBufferView | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(
    buffer instanceof ArrayBuffer ? buffer : buffer.buffer,
  );
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1)
    binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * The server hands the publicKey credential options back at the top level
 * (that's what `WebauthnSerializerFactory` produces). Older revisions of this
 * module expected a `{ publicKey: … }` wrapper — we defensively accept both
 * shapes so a stale service-worker cached index.html keeps working.
 */
export type ServerCreationOptions =
  | PublicKeyCredentialCreationOptionsJSON
  | { publicKey: PublicKeyCredentialCreationOptionsJSON };

export type ServerRequestOptions =
  | PublicKeyCredentialRequestOptionsJSON
  | { publicKey: PublicKeyCredentialRequestOptionsJSON };

function unwrapCreationOptions(
  body: ServerCreationOptions,
): PublicKeyCredentialCreationOptionsJSON {
  if (body && typeof body === 'object' && 'publicKey' in body && body.publicKey) {
    return body.publicKey;
  }
  return body as PublicKeyCredentialCreationOptionsJSON;
}

function unwrapRequestOptions(
  body: ServerRequestOptions,
): PublicKeyCredentialRequestOptionsJSON {
  if (body && typeof body === 'object' && 'publicKey' in body && body.publicKey) {
    return body.publicKey;
  }
  return body as PublicKeyCredentialRequestOptionsJSON;
}

/** The server sends base64url strings where the spec expects BufferSource. */
interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string;
  user: { id: string; name: string; displayName: string };
  rp: { id?: string; name: string };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: Array<{ id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  rpId?: string;
  timeout?: number;
  allowCredentials?: Array<{ id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }>;
  userVerification?: UserVerificationRequirement;
}

/** Safari throws a generic "Type error" when optional WebAuthn fields are null. */
function sanitizeAuthenticatorSelection(
  selection: AuthenticatorSelectionCriteria | undefined,
): AuthenticatorSelectionCriteria {
  const result: AuthenticatorSelectionCriteria = {
    userVerification: selection?.userVerification ?? 'preferred',
    residentKey: 'required',
    requireResidentKey: true,
  };
  if (selection?.authenticatorAttachment != null) {
    result.authenticatorAttachment = selection.authenticatorAttachment;
  }
  return result;
}

function decodeCreationOptions(
  input: PublicKeyCredentialCreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  const options: PublicKeyCredentialCreationOptions = {
    challenge: base64UrlToBuffer(input.challenge),
    rp: input.rp,
    user: {
      name: input.user.name,
      displayName: input.user.displayName,
      id: base64UrlToBuffer(input.user.id),
    },
    pubKeyCredParams: input.pubKeyCredParams,
    authenticatorSelection: sanitizeAuthenticatorSelection(input.authenticatorSelection),
  };

  if (input.timeout !== undefined) options.timeout = input.timeout;
  if (input.attestation !== undefined) options.attestation = input.attestation;
  if (input.excludeCredentials?.length) {
    options.excludeCredentials = input.excludeCredentials.map((c) => ({
      type: c.type,
      id: base64UrlToBuffer(c.id),
      ...(c.transports?.length ? { transports: c.transports } : {}),
    }));
  }

  return options;
}

function decodeRequestOptions(
  input: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    ...input,
    challenge: base64UrlToBuffer(input.challenge),
    allowCredentials: input.allowCredentials?.map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id),
    })),
  };
}

export interface RegistrationResult {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: AuthenticatorTransport[];
  };
  userAgent?: string;
}

export interface AssertionResult {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string;
  };
}

/**
 * Extract a readable message from any JSON error payload. Servers sometimes
 * hand back `{ error: { message: "…" } }` instead of a plain string — coerce
 * both shapes to something that doesn't render as "[object Object]".
 */
async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const raw = await res.text();
  let body: unknown = null;
  try {
    body = JSON.parse(raw);
  } catch {
    if (raw.trim()) return raw.trim();
  }

  if (body && typeof body === 'object') {
    const err = (body as { error?: unknown }).error;
    if (typeof err === 'string' && err !== '') return err;
    if (err && typeof err === 'object' && 'message' in err) {
      const m = (err as { message?: unknown }).message;
      if (typeof m === 'string' && m !== '') return m;
    }
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message !== '') return message;
  }

  return `${fallback} (${res.status})`;
}

/** Orchestrates a passkey registration ceremony. */
export async function startRegistration(token: string): Promise<RegistrationResult> {
  const optionsRes = await fetch(
    webauthnURL(`/webauthn/register/options?token=${encodeURIComponent(token)}`),
    { method: 'POST' },
  );
  if (!optionsRes.ok) {
    throw new Error(await extractErrorMessage(optionsRes, 'Unable to start registration'));
  }
  const body = (await optionsRes.json()) as ServerCreationOptions & {
    challengeToken: string;
  };
  if (!body.challengeToken) {
    throw new Error('Registration options missing challengeToken');
  }

  const credential = (await navigator.credentials.create({
    publicKey: decodeCreationOptions(unwrapCreationOptions(body)),
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('Passkey creation was cancelled.');

  const response = credential.response as AuthenticatorAttestationResponse;
  const payload: RegistrationResult = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports:
        typeof response.getTransports === 'function'
          ? (response.getTransports() as AuthenticatorTransport[])
          : undefined,
    },
    userAgent: navigator.userAgent,
  };

  const verifyRes = await fetch(
    webauthnURL(`/webauthn/register/verify?token=${encodeURIComponent(token)}`),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        challengeToken: body.challengeToken,
        response: payload,
      }),
    },
  );
  if (!verifyRes.ok) {
    throw new Error(await extractErrorMessage(verifyRes, 'Registration failed'));
  }

  return payload;
}

/** Orchestrates a passkey login ceremony against the Go backend. */
export async function startLogin(): Promise<{ userId: string; username: string | null }> {
  const optionsRes = await fetch(webauthnURL('/webauthn/login/options'), { method: 'POST' });
  if (!optionsRes.ok) {
    throw new Error(await extractErrorMessage(optionsRes, 'Unable to start login'));
  }
  const body = (await optionsRes.json()) as ServerRequestOptions & {
    challengeToken: string;
  };

  const credential = (await navigator.credentials.get({
    publicKey: decodeRequestOptions(unwrapRequestOptions(body)),
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('Passkey sign-in was cancelled.');

  const response = credential.response as AuthenticatorAssertionResponse;
  const payload: AssertionResult = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : undefined,
    },
  };

  const verifyRes = await fetch(webauthnURL('/webauthn/login/verify'), {
    method: 'POST',
    // Lets Go store the HttpOnly refresh cookie from this response.
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      challengeToken: body.challengeToken,
      response: payload,
    }),
  });
  if (!verifyRes.ok) {
    throw new Error(await extractErrorMessage(verifyRes, 'Sign-in failed'));
  }

  const result = (await verifyRes.json()) as {
    accessToken: string;
    user: { id: string; email: string | null; name: string };
  };
  // The refresh token in the body is ignored: the cookie is the only copy.
  setSession({ accessToken: result.accessToken });
  return { userId: result.user.id, username: result.user.email };
}

/** Reports whether the browser supports the WebAuthn platform authenticator. */
export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}
